// nearMissForm/NearMissForm.js
// This will be the component for the near miss form that the user will fill out.

import React, { useState } from "react";
import Select from "react-select";
import { shallow } from "zustand/shallow";
import { useNavigate } from "react-router-dom";
// State
import { useAppStore } from "../../store/appStore";
import useNearMissSocketAutosave from "../../hooks/useNearMissSocketAutosave";
// User
import { useUserState } from "../../context/user";
// Components
import { DelDatePicker } from "../common/DelDatePicker";
import { TimePickerInput } from "../common/TimePickerInput";
import { NearMissSignatureSection } from "./components/signatureSection";
import { PhotosComponent } from "../common/PhotosComponent";
import ConfirmationModal from "../common/ConfirmationModal";
import {
  BackToTableButton,
  SubmitSafetyFormButton,
} from "../landingPage/components/buttons/SafetyFormActions";
import { AutosaveIndicator } from "../common/AutosaveIndicator";
import CurrentDateTimeButton from "../common/CurrentDateTimeButton";

// Styles
import styles from "./NearMissForm.module.css";

export const NearMissForm = ({
  currentNearMiss,
  nearMissMode,
  nearMissFormUI,
  photosUI,
  rev,
  nearMissFormActions,
  setShowNearMissForm,
  autosaveErrors,
  autosaveErrorsActions,
  refreshListFunction = null,
  returnPath = null, // Optional: path to navigate back to (for reporting tab)
}) => {
  const navigate = useNavigate();
  const navigateToTab = useAppStore((s) => s.navigationActions.navigateToTab);
  // User Info
  const user = useUserState().currentUser;
  // Employees
  const employees = useAppStore((s) => s.employees.list, shallow);
  // Loading
  const showLoading = useAppStore((s) => s.loadingActions.showLoading);
  const hideLoading = useAppStore((s) => s.loadingActions.hideLoading);
  // Notification UI actions
  const { showNotification } = useAppStore(
    (s) => s.notificationActions,
    shallow
  );

  // WebSocket autosave hook - pass autosaveErrorsActions for error handling
  const { emitPatch, connectionStatus, saveStatus, pendingCount } =
    useNearMissSocketAutosave(currentNearMiss?.id, autosaveErrorsActions);

  // Near Miss Form Actions
  const submitNearMiss = useAppStore(
    (s) => s.safetyFormWorkflowActions.submitNearMiss,
    shallow
  );

  // State for unsaved photos confirmation modal
  const [showUnsavedPhotosConfirm, setShowUnsavedPhotosConfirm] = useState(false);

  // Key to force TimePickerInput re-render when autofilled
  const [timeKey, setTimeKey] = useState(0);

  // Back to table functions
  const handleBackToTable = () => {
    const target = returnPath || "/home/near_miss";
    navigate(target);
    refreshListFunction?.();
  };

  // Core submit logic (called directly or after unsaved photos confirmation)
  const executeSubmitNearMiss = async () => {
    showLoading("Submitting near miss...");
    const res = await submitNearMiss(currentNearMiss, user);
    hideLoading();
    if (res?.ok) {
      showNotification({
        type: "success",
        title: "Near Miss Submitted",
        message: "The near miss has been submitted successfully.",
      });
      handleBackToTable();
    } else if (res?.nearMissErrors) {
      showNotification({
        type: "validation",
        title: "Near Miss Validation Errors",
        errors: res.nearMissErrors,
      });
    }
  };

  // Submit near miss function
  const handleSubmitNearMiss = async () => {
    // Check for unsaved selected photos (last guard)
    const selectedPhotos = photosUI?.selectedPhotos || [];
    if (selectedPhotos.length > 0) {
      setShowUnsavedPhotosConfirm(true);
      return;
    }

    await executeSubmitNearMiss();
  };

  // Helper to get autosave field error for near miss fields
  const getAutosaveFieldError = (fieldName) => {
    const key = `near_miss_main.${fieldName}`;
    return autosaveErrors?.fieldErrors?.[key] || null;
  };

  // Get submission errors and autosave errors
  const submissionErrors = currentNearMiss.errors || {};

  // Get autosave field errors
  const incidentDateAutosaveError = getAutosaveFieldError("incident_date");
  const incidentTimeAutosaveError = getAutosaveFieldError("incident_time");
  const locationAutosaveError = getAutosaveFieldError("location");
  const siteAutosaveError = getAutosaveFieldError("site");
  const reportedToAutosaveError = getAutosaveFieldError("reported_to");
  const reportedByAutosaveError = getAutosaveFieldError("reported_by");
  const nearMissDescriptionAutosaveError = getAutosaveFieldError("near_miss_description");
  const contributingFactorsAutosaveError = getAutosaveFieldError("contributing_factors");
  const actionsTakenAutosaveError = getAutosaveFieldError("actions_taken");
  const sagCorrectiveActionsAutosaveError = getAutosaveFieldError("sag_corrective_actions");
  const immediateAssYesDescriptionAutosaveError = getAutosaveFieldError("immediate_ass_yes_description");

  // Combine errors (autosave errors take precedence as they're more recent)
  const nearMissDateError = (incidentDateAutosaveError || submissionErrors.incidentDate) ? styles.error : "";
  const nearMissTimeError = (incidentTimeAutosaveError || submissionErrors.incidentTime) ? styles.error : "";
  const locationError = (locationAutosaveError || submissionErrors.location) ? styles.error : "";
  const siteError = (siteAutosaveError || submissionErrors.site) ? styles.error : "";
  const reportedToError = (reportedToAutosaveError || submissionErrors.reportedTo) ? styles.errorSelectContainer : "";
  const reportedByError = (reportedByAutosaveError || submissionErrors.reportedBy) ? styles.errorSelectContainer : "";
  const nearMissDescriptionError = (nearMissDescriptionAutosaveError || submissionErrors.nearMissDescription) ? styles.error : "";
  const contributingFactorsError = (contributingFactorsAutosaveError || submissionErrors.contributingFactors) ? styles.error : "";
  const actionsTakenError = (actionsTakenAutosaveError || submissionErrors.actionsTaken) ? styles.error : "";
  const sagCorrectiveActionsError = (sagCorrectiveActionsAutosaveError || submissionErrors.sag_corrective_actions) ? styles.error : "";
  const immediateAssYesDescriptionError = (immediateAssYesDescriptionAutosaveError || submissionErrors.immediateAssYesDescription) ? styles.error : "";

  // Return the near miss form
  return (
    <div className={styles.nearMissFormContainer}>
      {/* Tab Bar */}
      <div className={styles.tabBar}>
        <button
          className={`${styles.tabButton} ${
            nearMissFormUI.activeTab === "form" ? styles.activeTab : ""
          }`}
          onClick={() => nearMissFormActions.setActiveTab("form")}
          type="button"
        >
          Near Miss
        </button>
        <button
          className={`${styles.tabButton} ${
            nearMissFormUI.activeTab === "photos" ? styles.activeTab : ""
          }`}
          onClick={() => nearMissFormActions.setActiveTab("photos")}
          type="button"
        >
          Photos{" "}
          {currentNearMiss.photos?.length > 0 &&
            `(${currentNearMiss.photos.length})`}
        </button>
      </div>

      {/* Tab Content */}
      {nearMissFormUI.activeTab === "form" ? (
      <div className={styles.nearMissFormBody}>
        <div className={styles.formContentWrapper}>
          {/* Incident Information Section */}
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Incident Information</h2>
            <div className={styles.dateTimeRow}>
              <div className={styles.formField}>
                <label htmlFor="nearMissDate">Date of Incident</label>
                <DelDatePicker
                  id="nearMissDate"
                  selected={currentNearMiss.incident_date || null}
                  onChange={(date) =>
                    nearMissFormActions.updateIncidentDate(date, emitPatch)
                  }
                  className={`${styles.dateInput} ${nearMissDateError}`}
                />
                {(incidentDateAutosaveError || submissionErrors.incidentDate) && (
                  <span className={styles.errorText}>
                    {incidentDateAutosaveError || submissionErrors.incidentDate}
                  </span>
                )}
              </div>
              <div className={styles.formField}>
                <label htmlFor="nearMissTime">Time of Incident</label>
                <TimePickerInput
                  key={timeKey}
                  id="nearMissTime"
                  interval={1}
                  value={currentNearMiss.incident_time || ""}
                  onChange={(time) =>
                    nearMissFormActions.updateIncidentTime(time, emitPatch)
                  }
                  className={`${styles.timeInput} ${nearMissTimeError}`}
                />
                {(incidentTimeAutosaveError || submissionErrors.incidentTime) && (
                  <span className={styles.errorText}>
                    {incidentTimeAutosaveError || submissionErrors.incidentTime}
                  </span>
                )}
              </div>
              <CurrentDateTimeButton
                onDateChange={(date) =>
                  nearMissFormActions.updateIncidentDate(date, emitPatch)
                }
                onTimeChange={(time) => {
                  nearMissFormActions.updateIncidentTime(time, emitPatch);
                  setTimeKey((k) => k + 1);
                }}
                disabled={nearMissMode === "view"}
              />
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formField}>
                <label htmlFor="nearMissLocation">Location</label>
                <input
                  type="text"
                  id="nearMissLocation"
                  value={currentNearMiss.location || ""}
                  onChange={(e) =>
                    nearMissFormActions.updateLocation(e.target.value, null)
                  }
                  onBlur={(e) =>
                    nearMissFormActions.updateLocation(e.target.value, emitPatch)
                  }
                  className={locationError}
                />
                {(locationAutosaveError || submissionErrors.location) && (
                  <span className={styles.errorText}>
                    {locationAutosaveError || submissionErrors.location}
                  </span>
                )}
              </div>
              <div className={styles.formField}>
                <label htmlFor="nearMissSite">Site</label>
                <input
                  type="text"
                  id="nearMissSite"
                  value={currentNearMiss.site || ""}
                  onChange={(e) =>
                    nearMissFormActions.updateSite(e.target.value, null)
                  }
                  onBlur={(e) =>
                    nearMissFormActions.updateSite(e.target.value, emitPatch)
                  }
                  className={siteError}
                />
                {(siteAutosaveError || submissionErrors.site) && (
                  <span className={styles.errorText}>
                    {siteAutosaveError || submissionErrors.site}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Reporting Information Section */}
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Reporting Information</h2>
            <div className={styles.formGrid}>
              <div className={`${styles.formField} ${styles.desktopFormField}`}>
                <label htmlFor="reportedTo">Reported To</label>
                <Select
                  id="reportedTo"
                  className={reportedToError}
                  value={
                    currentNearMiss.reported_to
                      ? {
                          value: currentNearMiss.reported_to,
                          label: currentNearMiss.reported_to_name,
                        }
                      : null
                  }
                  options={employees.map((employee) => ({
                    value: employee.employee_id,
                    label: employee.label,
                  }))}
                  onChange={(selected) => {
                    nearMissFormActions.updateReportedTo(selected, emitPatch);
                  }}
                  placeholder={
                    (reportedToAutosaveError || submissionErrors.reportedTo) ? (
                      <span className={styles.errorText}>
                        {reportedToAutosaveError || submissionErrors.reportedTo}
                      </span>
                    ) : (
                      "Select Employee"
                    )
                  }
                  isSearchable={true}
                  isClearable={true}
                />
              </div>
            </div>
            <div className={styles.formGrid}>
              <div className={`${styles.formField} ${styles.desktopFormField}`}>
                <label htmlFor="reportedBy">Reported By</label>
                <Select
                  id="reportedBy"
                  className={reportedByError}
                  value={
                    currentNearMiss.reported_by
                      ? {
                          value: currentNearMiss.reported_by,
                          label: currentNearMiss.reported_by_name,
                        }
                      : null
                  }
                  options={employees.map((employee) => ({
                    value: employee.employee_id,
                    label: employee.label,
                    job_title: employee.job_title,
                  }))}
                  onChange={(selected) => {
                    nearMissFormActions.updateReportedBy(selected, emitPatch);
                  }}
                  placeholder={
                    (reportedByAutosaveError || submissionErrors.reportedBy) ? (
                      <span className={styles.errorText}>
                        {reportedByAutosaveError || submissionErrors.reportedBy}
                      </span>
                    ) : (
                      "Select Employee"
                    )
                  }
                  isSearchable={true}
                  isClearable={true}
                />
              </div>
              <div className={styles.formField}>
                <label htmlFor="jobTitle">Job Title</label>
                <input
                  type="text"
                  id="jobTitle"
                  value={currentNearMiss.job_title || ""}
                  onChange={(e) =>
                    nearMissFormActions.updateJobTitle(e.target.value, null)
                  }
                  onBlur={(e) =>
                    nearMissFormActions.updateJobTitle(e.target.value, emitPatch)
                  }
                />
              </div>
            </div>
          </div>

          {/* Description Section */}
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Description of Near Miss</h2>
            <p className={styles.sectionDescription}>
              Briefly describe what happened and how the near miss occurred.
            </p>
            <textarea
              className={`${styles.textareaField} ${nearMissDescriptionError}`}
              id="nearMissDescription"
              value={currentNearMiss.near_miss_description || ""}
              onChange={(e) =>
                nearMissFormActions.updateNearMissDescription(e.target.value, null)
              }
              onBlur={(e) =>
                nearMissFormActions.updateNearMissDescription(e.target.value, emitPatch)
              }
              rows={6}
            />
            {(nearMissDescriptionAutosaveError || submissionErrors.nearMissDescription) && (
              <span className={styles.errorText}>
                {nearMissDescriptionAutosaveError || submissionErrors.nearMissDescription}
              </span>
            )}
          </div>

          {/* Potential Hazards Section */}
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Potential Hazards Involved</h2>
            {submissionErrors.potHazards && (
              <div className={styles.errorMessage}>{submissionErrors.potHazards}</div>
            )}
            <div className={styles.checkboxGrid}>
              <div className={styles.checkboxItem}>
                <label htmlFor="potHazStf">Slips, Trips, or Falls</label>
                <input
                  type="checkbox"
                  id="potHazStf"
                  checked={currentNearMiss.pot_haz_stf}
                  onChange={(e) =>
                    nearMissFormActions.updatePotHazSTF(e.target.checked, emitPatch)
                  }
                />
              </div>
              <div className={styles.checkboxItem}>
                <label htmlFor="potHazFo">Falling Objects</label>
                <input
                  type="checkbox"
                  id="potHazFo"
                  checked={currentNearMiss.pot_haz_fo}
                  onChange={(e) =>
                    nearMissFormActions.updatePotHazFO(e.target.checked, emitPatch)
                  }
                />
              </div>
              <div className={styles.checkboxItem}>
                <label htmlFor="potHazCe">Chemical Exposure</label>
                <input
                  type="checkbox"
                  id="potHazCe"
                  checked={currentNearMiss.pot_haz_ce}
                  onChange={(e) =>
                    nearMissFormActions.updatePotHazCE(e.target.checked, emitPatch)
                  }
                />
              </div>
              <div className={styles.checkboxItem}>
                <label htmlFor="potHazEh">Electrical Hazards</label>
                <input
                  type="checkbox"
                  id="potHazEh"
                  checked={currentNearMiss.pot_haz_eh}
                  onChange={(e) =>
                    nearMissFormActions.updatePotHazEH(e.target.checked, emitPatch)
                  }
                />
              </div>
              <div className={styles.checkboxItem}>
                <label htmlFor="potHazEf">Equipment Failure</label>
                <input
                  type="checkbox"
                  id="potHazEf"
                  checked={currentNearMiss.pot_haz_ef}
                  onChange={(e) =>
                    nearMissFormActions.updatePotHazEF(e.target.checked, emitPatch)
                  }
                />
              </div>
              <div className={styles.checkboxItem}>
                <label htmlFor="potHazIppe">Inadequate PPE</label>
                <input
                  type="checkbox"
                  id="potHazIppe"
                  checked={currentNearMiss.pot_haz_ippe}
                  onChange={(e) =>
                    nearMissFormActions.updatePotHazIPPE(e.target.checked, emitPatch)
                  }
                />
              </div>
              <div className={styles.checkboxItem}>
                <label htmlFor="potHazMhi">Manual Handling Injury</label>
                <input
                  type="checkbox"
                  id="potHazMhi"
                  checked={currentNearMiss.pot_haz_mhi}
                  onChange={(e) =>
                    nearMissFormActions.updatePotHazMHI(e.target.checked, emitPatch)
                  }
                />
              </div>
              <div className={styles.checkboxItem}>
                <label htmlFor="potHazFeh">Fire or Explosion Hazard</label>
                <input
                  type="checkbox"
                  id="potHazFeh"
                  checked={currentNearMiss.pot_haz_feh}
                  onChange={(e) =>
                    nearMissFormActions.updatePotHazFEH(e.target.checked, emitPatch)
                  }
                />
              </div>
              <div className={styles.checkboxItem}>
                <label htmlFor="potHazCsi">Confined Space Issues</label>
                <input
                  type="checkbox"
                  id="potHazCsi"
                  checked={currentNearMiss.pot_haz_csi}
                  onChange={(e) =>
                    nearMissFormActions.updatePotHazCSI(e.target.checked, emitPatch)
                  }
                />
              </div>
            </div>
            <div className={styles.formField} style={{ marginTop: "1rem" }}>
              <label htmlFor="potHazOther">
                Other Hazards (please specify)
              </label>
              <input
                type="text"
                id="potHazOther"
                value={currentNearMiss.pot_haz_other || ""}
                onChange={(e) =>
                  nearMissFormActions.updatePotHazOther(e.target.value, null)
                }
                onBlur={(e) =>
                  nearMissFormActions.updatePotHazOther(e.target.value, emitPatch)
                }
              />
            </div>
          </div>

          {/* Contributing Factors Section */}
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Contributing Factors</h2>
            <p className={styles.sectionDescription}>
              What factors contributed to the near miss? Ex: Lack of training,
              poor lighting, equipment malfunction, etc.
            </p>
            <textarea
              className={`${styles.textareaField} ${contributingFactorsError}`}
              id="contributingFactors"
              value={currentNearMiss.contributing_factors || ""}
              onChange={(e) =>
                nearMissFormActions.updateContributingFactors(e.target.value, null)
              }
              onBlur={(e) =>
                nearMissFormActions.updateContributingFactors(e.target.value, emitPatch)
              }
              rows={4}
            />
            {(contributingFactorsAutosaveError || submissionErrors.contributingFactors) && (
              <span className={styles.errorText}>
                {contributingFactorsAutosaveError || submissionErrors.contributingFactors}
              </span>
            )}
          </div>

          {/* Actions Taken Section */}
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Actions Taken (if any)</h2>
            <p className={styles.sectionDescription}>
              What was done to mitigate the hazard or prevent an incident?
            </p>
            <textarea
              className={`${styles.textareaField} ${actionsTakenError}`}
              id="actionsTaken"
              value={currentNearMiss.actions_taken || ""}
              onChange={(e) =>
                nearMissFormActions.updateActionsTaken(e.target.value, null)
              }
              onBlur={(e) =>
                nearMissFormActions.updateActionsTaken(e.target.value, emitPatch)
              }
              rows={4}
            />
            {(actionsTakenAutosaveError || submissionErrors.actionsTaken) && (
              <span className={styles.errorText}>
                {actionsTakenAutosaveError || submissionErrors.actionsTaken}
              </span>
            )}
          </div>

          {/* Suggested Corrective Actions Section */}
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>
              Suggested Corrective Actions
            </h2>
            <p className={styles.sectionDescription}>
              What improvements or changes could be made to prevent similar near
              misses in the future?
            </p>
            <textarea
              className={`${styles.textareaField} ${sagCorrectiveActionsError}`}
              id="sagCorrectiveActions"
              value={currentNearMiss.sag_corrective_actions || ""}
              onChange={(e) =>
                nearMissFormActions.updateSagCorrectiveActions(e.target.value, null)
              }
              onBlur={(e) =>
                nearMissFormActions.updateSagCorrectiveActions(e.target.value, emitPatch)
              }
              rows={4}
            />
            {(sagCorrectiveActionsAutosaveError || submissionErrors.sag_corrective_actions) && (
              <span className={styles.errorText}>
                {sagCorrectiveActionsAutosaveError || submissionErrors.sag_corrective_actions}
              </span>
            )}
          </div>

          {/* Immediate Assistance Section */}
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>
              Immediate Assistance Required?
            </h2>
            {submissionErrors.immediateAss && (
              <div className={styles.errorMessage}>{submissionErrors.immediateAss}</div>
            )}
            <div className={styles.radioGroup}>
              <div className={styles.radioItem}>
                <input
                  type="checkbox"
                  id="immediateAssYes"
                  checked={currentNearMiss.immediate_ass_yes}
                  onChange={(e) =>
                    nearMissFormActions.updateImmediateAssYes(e.target.checked, emitPatch)
                  }
                />
                <label htmlFor="immediateAssYes">Yes</label>
              </div>
              <div className={styles.radioItem}>
                <input
                  type="checkbox"
                  id="immediateAssNo"
                  checked={currentNearMiss.immediate_ass_no}
                  onChange={(e) =>
                    nearMissFormActions.updateImmediateAssNo(e.target.checked, emitPatch)
                  }
                />
                <label htmlFor="immediateAssNo">No</label>
              </div>
            </div>
            {currentNearMiss.immediate_ass_yes && (
              <div className={styles.formField} style={{ marginTop: "1rem" }}>
                <label htmlFor="immediateAssYesDescription">
                  Please describe the assistance required
                </label>
                <textarea
                  className={`${styles.textareaField} ${immediateAssYesDescriptionError}`}
                  id="immediateAssYesDescription"
                  value={currentNearMiss.immediate_ass_yes_description || ""}
                  onChange={(e) =>
                    nearMissFormActions.updateImmediateAssYesDescription(
                      e.target.value,
                      null
                    )
                  }
                  onBlur={(e) =>
                    nearMissFormActions.updateImmediateAssYesDescription(
                      e.target.value,
                      emitPatch
                    )
                  }
                  rows={3}
                />
                {(immediateAssYesDescriptionAutosaveError || submissionErrors.immediateAssYesDescription) && (
                  <span className={styles.errorText}>
                    {immediateAssYesDescriptionAutosaveError || submissionErrors.immediateAssYesDescription}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Signature Section */}
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Signature</h2>
            <NearMissSignatureSection
              employeeName={currentNearMiss.signature_employee_name || user.name}
              signatureDate={currentNearMiss.signature_date}
              signatureInfoImage={currentNearMiss.signature}
              nearMissMode={nearMissMode}
              actions={nearMissFormActions}
              ui={nearMissFormUI}
              emitPatch={emitPatch}
              user={user}
            />
          </div>
        </div>
      </div>
      ) : (
        <PhotosComponent
          photos={currentNearMiss.photos || []}
          photosUI={photosUI}
          photosActions={nearMissFormActions.photos}
          emitPatch={emitPatch}
          isViewMode={nearMissMode === "view"}
          connectionStatus={connectionStatus || "connected"}
          canDownload={true}
          formId={currentNearMiss.id}
          formType="near_miss"
          showLoading={showLoading}
          hideLoading={hideLoading}
          showNotification={showNotification}
        />
      )}

      {/* Unsaved photos confirmation modal */}
      <ConfirmationModal
        isOpen={showUnsavedPhotosConfirm}
        onCancel={() => setShowUnsavedPhotosConfirm(false)}
        onConfirm={async () => {
          setShowUnsavedPhotosConfirm(false);
          await executeSubmitNearMiss();
        }}
        title="Unsaved Photos"
        message={`You have ${photosUI?.selectedPhotos?.length || 0} selected photo(s) that have not been saved. If you continue, these photos will not be included.`}
        confirmText="Continue Without Photos"
        cancelText="Go Back"
      />

      {/* Action Buttons Container */}
      <div className={styles.actionButtonsContainer}>
        <div className={styles.rightSideTableActions}>
          <BackToTableButton
            backToTableFunction={handleBackToTable}
            tableName="Near Miss"
          />
          {(nearMissMode === "new" || nearMissMode === "edit") && (
            <AutosaveIndicator
              connectionStatus={connectionStatus}
              saveStatus={saveStatus}
              pendingCount={pendingCount}
            />
          )}
        </div>

        <div className={styles.leftSideTableActions}>
          {nearMissMode !== "view" && nearMissMode !== "approve" && (
            <SubmitSafetyFormButton submitFunction={handleSubmitNearMiss} />
          )}
        </div>
      </div>
    </div>
  );
};
